import type { SpringValue } from '@react-spring/web';
import { animated, easings, useTransition } from '@react-spring/web';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { range } from '#src/lib/arrays.ts';

const MAX_ICONS_TO_ANIMATE = 50;

type AnimationStyle = {
  opacity: SpringValue<number>;
  progress: SpringValue<number>;
};

const FlyingIcon = styled(animated.div)`
  position: fixed;
  pointer-events: none;
  left: 0;
  top: 0;
`;

type Props = {
  icon: ReactNode;
  count: number;
  onAnimationComplete: () => void;
  targetRef: React.RefObject<HTMLElement | null>;
};

export const CurrencyAnimation = ({
  icon,
  count: propCount,
  onAnimationComplete,
  targetRef,
}: Props) => {
  const [start, setStart] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [targetCenter, setTargetCenter] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Start animation when propCount changes
  if (count === 0 && propCount > 0) {
    const length = Math.max(
      1,
      Math.round(Math.min(100, Math.log10(propCount) * 10))
    );

    setTargetCenter(null);
    setCount(length);
    setStart(0);
  }

  // reset animation when propCount is set to 0
  if (propCount === 0 && start > 0) {
    setTargetCenter(null);
    setCount(0);
    setStart(0);
  }

  useLayoutEffect(() => {
    if (count === 0) {
      return;
    }
    const el = targetRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    setTargetCenter({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
  }, [count, targetRef]);

  // trigger callback when we have animated all the icons
  useEffect(() => {
    if (count !== 0 && start > 0 && start >= count - 1) {
      onAnimationComplete();
    }
  }, [count, start, onAnimationComplete]);

  const iconsArray = useMemo(() => {
    if (start !== undefined) {
      const length = Math.min(
        Math.max(0, count - start - 1),
        MAX_ICONS_TO_ANIMATE
      );

      return range(length).map(v => v + start);
    }
    return [];
  }, [start, count]);

  const transitions = useTransition(iconsArray, {
    from: {
      opacity: 0,
      progress: 0,
    },
    enter: {
      to: [
        {
          opacity: 1,
          progress: 0.5,
        },
        {
          opacity: 0.9,
          progress: 0.9,
        },
        {
          opacity: 0,
          progress: 1,
        },
      ],
      config: {
        duration: 2000 / count,
        easing: easings.linear,
      },
    },
    leave: {
      to: {
        opacity: 0,
        progress: 1,
      },
    },
    trail: MAX_ICONS_TO_ANIMATE,
    onRest: () => {
      setStart(s => (s ?? 0) + 1);
    },
  });

  if (count === 0 || targetCenter === null) {
    return null;
  }

  const { x: targetX, y: targetY } = targetCenter;

  return createPortal(
    <div
      style={
        {
          pointerEvent: 'none',
          isolation: 'isolate',
          '--start-x': `${targetX}px`,
          '--start-y': `${targetY}px`,
          '--target-x': `${targetX}px`,
          '--target-y': `${targetY}px`,
        } as React.CSSProperties
      }
    >
      {transitions((style: AnimationStyle, index) => {
        return (
          <FlyingIcon
            key={index}
            style={{
              opacity: style.opacity.to((x: number) => x),
              transform: style.progress.to(
                (p: number) =>
                  `translate(
                    calc(-50% + var(--target-x) + ${1 - p} * ( var(--click-x) + 10px * cos(${index} * pi / 4) - var(--start-x) ) ),
                    calc(-50% + var(--target-y) + ${1 - p} * ( var(--click-y) + 10px * sin(${index} * pi / 4) - var(--start-y) ) )
                  )`
              ),
            }}
          >
            {icon}
          </FlyingIcon>
        );
      })}
    </div>,
    document.body
  );
};
